import BreadCrumb from "Common/BreadCrumb";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus, Search, Eye } from "lucide-react";
import TableContainer from "Common/TableContainer";
import DeleteModal from "Common/DeleteModal";
import Loadr from "Common/Components/Loader";
import { toast, ToastContainer } from "react-toastify";
import { deleteGames } from "services/apiGames";
import { useGames } from "./useGames";

const Games = ({ showAddButton = true }) => {
  const { games, isLoading } = useGames();
  const [data, setData] = useState([]);
  const [eventData, setEventData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  useEffect(() => {
    setData(games || []);
  }, [games]);

  const deleteToggle = () => setDeleteModal(!deleteModal);

  const onClickDelete = (cell) => {
    setDeleteModal(true);
    setEventData(cell?.id ? cell : null);
  };

  const handleDelete = async () => {
    if (eventData?.id) {
      try {
        await deleteGames([eventData.id]);
        toast.success("Game successfully deleted");
        setDeleteModal(false);
        setEventData(null);
      } catch (error) {
        toast.error(error.message || "Failed to delete game");
      }
    } else {
      toast.error("No game selected for deletion");
    }
  };

  const DateFormat = ({ item }) => {
    if (!item) return "—";
    const [year, month, day] = Array.isArray(item) ? item : [];
    if (year == null) return "—";
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  const Status = ({ item }) => {
    switch (item) {
      case false:
        return (
          <span className="delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-red-100 border-red-200 text-red-500 dark:bg-red-500/20 dark:border-red-500/20">
            Disabled
          </span>
        );
      default:
        return (
          <span className="delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-green-100 border-green-200 text-green-500 dark:bg-green-500/20 dark:border-green-500/20">
            Enabled
          </span>
        );
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Game ID",
        accessorKey: "referenceId",
        enableColumnFilter: false,
        cell: (cell) => (
          <span className="text-custom-500">{cell.getValue()}</span>
        ),
      },
      {
        header: "Game Title",
        accessorKey: "title",
        enableColumnFilter: false,
        cell: (cell) => (
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-full shrink-0 bg-slate-100">
              <img
                src={cell.row.original.image}
                alt=""
                className="h-10 rounded-full object-contain"
              />
            </div>
            <h6 className="grow">{cell.getValue()}</h6>
          </div>
        ),
      },
      {
        header: "Price",
        accessorKey: "amountPerSlot",
        enableColumnFilter: false,
      },
      {
        header: "Game Type",
        accessorKey: "type",
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell) => <Status item={cell.getValue()} />,
      },
      {
        header: "Created Date",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (cell) => <DateFormat item={cell.getValue()} />,
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <div className="flex gap-2">
              <Link
                to={`/games/view/${row.id}`}
                className="flex items-center justify-center size-8 rounded-md bg-slate-100 text-slate-500 hover:text-blue-600 hover:bg-blue-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-blue-500/20 dark:hover:text-blue-400"
                title="View"
              >
                <Eye className="size-4" />
              </Link>
              <Link
                to={`/games/edit/${row.id}`}
                className="flex items-center justify-center size-8 rounded-md bg-slate-100 text-slate-500 hover:text-custom-500 hover:bg-custom-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-custom-500/20 dark:hover:text-custom-500"
                title="Edit"
              >
                <Pencil className="size-4" />
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb title="Games" pageTitle="Dashboard" />
      {isLoading && <Loadr />}
      <DeleteModal show={deleteModal} onHide={deleteToggle} onDelete={handleDelete} />
      <ToastContainer closeButton={false} limit={1} />
      <div className="card" id="employeeTable">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              Games (<b>{data?.length ?? 0}</b>)
            </h6>
            {showAddButton && (
              <div className="shrink-0">
                <Link
                  to="/games/create"
                  type="button"
                  className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
                >
                  <Plus className="inline-block size-4" />{" "}
                  <span className="align-middle">Add Game</span>
                </Link>
              </div>
            )}
          </div>
          {data && data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={data}
              customPageSize={7}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pl-5 border-y border-slate-200 dark:border-zink-500"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <div className="noresult">
              <div className="py-6 text-center">
                <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                <p className="mb-0 text-slate-500 dark:text-zink-200">
                  No games yet. Add a game to get started.
                </p>
                {showAddButton && (
                  <Link to="/games/create" className="btn bg-custom-500 text-white mt-4 inline-block">
                    Add Game
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Games;
